/**
 * Affiliate card on the account-detail page (OCP-490).
 *
 * Turns an existing portal account into an affiliate: grant/revoke, edit
 * the per-affiliate commission override + payout details, and surface the
 * ready-to-share `?ref=` link the operator hands the influencer.
 *
 * Self-contained: loads its own `GET /v1/admin/accounts/:id/affiliate`.
 * A load failure hides the card (returns null) rather than erroring the
 * whole page. Payout details are WRITE-ONLY - we only ever see whether
 * they are on file, never the plaintext (the reveal is OCP-384c).
 *
 * Earnings + payout history are owned by OCP-384b/384c; this card only
 * owns grant + config + the share link + live referral stats.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { api, ApiError } from '../lib/api';
import { useTransientState } from '../lib/useTransientState';

type CommissionModel = 'pct_first_payment' | 'flat';
type PayoutMethod = 'sepa' | 'paypal' | 'other';

export interface AffiliateDto {
  status: 'active' | 'paused';
  commission_model: CommissionModel | null;
  commission_pct: number | null;
  commission_flat_cents: number | null;
  payout_method: PayoutMethod | null;
  payout_details_set: boolean;
  notes: string | null;
  code: string | null;
  share_link: string | null;
  campaign_id: string | null;
  granted_by: string | null;
  granted_at: string;
  created_at: string;
  updated_at: string;
  stats: { referred: number; converted: number };
  /** OCP-492 - commission balances by currency (cents). */
  earnings: {
    currencies: Array<{
      currency: string;
      holding_cents: number;
      eligible_cents: number;
      paid_cents: number;
    }>;
  };
}

export function AffiliateCard({ customerId }: { customerId: string }) {
  const [affiliate, setAffiliate] = useState<AffiliateDto | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'hidden'>('loading');
  const [editing, setEditing] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useTransientState(false, 1500);

  const load = useCallback(async () => {
    try {
      const r = await api<{ affiliate: AffiliateDto | null }>(
        `/v1/admin/accounts/${customerId}/affiliate`,
      );
      setAffiliate(r.affiliate);
      setState('ready');
    } catch {
      // Unknown account / transient failure - hide the card rather than
      // erroring the whole account-detail page.
      setState('hidden');
    }
  }, [customerId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleRevoke() {
    setBusy(true);
    setError(null);
    try {
      await api(`/v1/admin/accounts/${customerId}/affiliate`, { method: 'DELETE' });
      setConfirmRevoke(false);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? `Revoke failed (HTTP ${err.status}).` : 'Revoke failed.');
    } finally {
      setBusy(false);
    }
  }

  async function handleCopy(link: string) {
    try {
      await navigator.clipboard?.writeText(link);
      setCopied(true);
    } catch {
      // Clipboard blocked (permissions / insecure context) - no-op.
    }
  }

  if (state === 'hidden') return null;

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Affiliate</h2>
        {state === 'ready' && affiliate && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-xs text-brand-500 hover:text-brand-600 hover:underline"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={() => setConfirmRevoke(true)}
              className="text-xs text-red-600 hover:text-red-700 hover:underline"
            >
              Revoke
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="mt-2 text-xs text-red-700 dark:text-red-300">{error}</div>
      )}

      {state === 'loading' && (
        <div className="mt-3 text-sm text-gray-500">Loading…</div>
      )}

      {state === 'ready' && !affiliate && (
        <div className="mt-3 flex flex-col items-start gap-3 rounded border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 px-3 py-4">
          <span className="text-sm text-gray-500">
            This account is not an affiliate. Grant affiliate status to mint its share link.
          </span>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="rounded bg-brand-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-600"
          >
            Grant affiliate status
          </button>
        </div>
      )}

      {state === 'ready' && affiliate && (
        <div className="mt-3 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <AffiliateStatusPill status={affiliate.status} />
            {affiliate.code && (
              <span className="font-mono text-sm text-gray-800 dark:text-gray-200">
                {affiliate.code}
              </span>
            )}
            <span className="ml-auto text-xs text-gray-500">
              {affiliate.stats.referred} referred · {affiliate.stats.converted} converted
            </span>
          </div>

          {affiliate.share_link && (
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500">Share link</div>
              <div className="mt-1 flex items-center gap-2">
                <code className="min-w-0 flex-1 truncate rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 px-2 py-1 text-xs">
                  {affiliate.share_link}
                </code>
                <button
                  type="button"
                  onClick={() => void handleCopy(affiliate.share_link!)}
                  className="shrink-0 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
                >
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
          )}

          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
            <Field label="Commission" value={commissionSummary(affiliate)} />
            <Field
              label="Payout method"
              value={affiliate.payout_method ? affiliate.payout_method.toUpperCase() : '-'}
            />
            <Field
              label="Payout details"
              value={affiliate.payout_details_set ? 'on file (encrypted)' : 'none'}
            />
          </dl>

          {affiliate.notes && (
            <div className="text-xs text-gray-500">Note: {affiliate.notes}</div>
          )}

          {/* OCP-492 - commission balances. OCP-493 - payout workflow. */}
          <AffiliateEarnings earnings={affiliate.earnings} />
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-[11px] text-gray-400 dark:text-gray-500">
              Held = still inside the refund window · Eligible = matured, payable · Paid = paid out.
            </div>
            {hasEligible(affiliate.earnings) && (
              <Link
                to="/affiliate-payouts"
                className="shrink-0 text-xs font-medium text-brand-500 hover:text-brand-600 hover:underline"
              >
                Pay out eligible →
              </Link>
            )}
          </div>

          {confirmRevoke && (
            <div className="flex items-center gap-2 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-xs">
              <span className="text-red-700 dark:text-red-300">
                Revoke affiliate status? The share link stays reserved for a later re-grant.
              </span>
              <button
                type="button"
                disabled={busy}
                onClick={() => void handleRevoke()}
                className="ml-auto rounded bg-red-600 px-2 py-1 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {busy ? 'Revoking…' : 'Yes, revoke'}
              </button>
              <button
                type="button"
                onClick={() => setConfirmRevoke(false)}
                className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-gray-700 dark:text-gray-300"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      {editing && (
        <AffiliateFormModal
          customerId={customerId}
          initial={affiliate}
          onCancel={() => setEditing(false)}
          onDone={async () => {
            setEditing(false);
            await load();
          }}
        />
      )}
    </div>
  );
}

/** True when the affiliate has any matured, payable (eligible) balance. */
function hasEligible(earnings: AffiliateDto['earnings']): boolean {
  return earnings.currencies.some((c) => c.eligible_cents > 0);
}

function AffiliateEarnings({ earnings }: { earnings: AffiliateDto['earnings'] }) {
  if (earnings.currencies.length === 0) {
    return (
      <div className="rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 px-3 py-2 text-xs text-gray-500">
        No commissions yet.
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {earnings.currencies.map((c) => (
        <div key={c.currency} className="grid grid-cols-3 gap-2">
          <EarningsCell label="Held" cents={c.holding_cents} currency={c.currency} />
          <EarningsCell label="Eligible" cents={c.eligible_cents} currency={c.currency} strong />
          <EarningsCell label="Paid" cents={c.paid_cents} currency={c.currency} />
        </div>
      ))}
    </div>
  );
}

function EarningsCell({
  label,
  cents,
  currency,
  strong,
}: {
  label: string;
  cents: number;
  currency: string;
  strong?: boolean;
}) {
  return (
    <div className="rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-2 py-1.5">
      <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
      <div
        className={`mt-0.5 text-sm tabular-nums ${
          strong ? 'font-semibold text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'
        }`}
      >
        {formatMoney(cents, currency)}
      </div>
    </div>
  );
}

/** Cents -> localized currency string; falls back to `<amount> <CODE>` on an unknown code. */
export function formatMoney(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

function commissionSummary(a: AffiliateDto): string {
  if (a.commission_model === 'pct_first_payment') {
    return a.commission_pct === null
      ? 'campaign default %'
      : `${a.commission_pct}% of first payment`;
  }
  if (a.commission_model === 'flat') {
    return a.commission_flat_cents === null
      ? 'campaign default flat'
      : `€${(a.commission_flat_cents / 100).toFixed(2)} flat`;
  }
  return 'inherit campaign default';
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-0.5 text-sm text-gray-900 dark:text-gray-100">{value}</dd>
    </div>
  );
}

function AffiliateStatusPill({ status }: { status: 'active' | 'paused' }) {
  const cls =
    status === 'active'
      ? 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300'
      : 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200';
  return <span className={`rounded border px-2 py-0.5 text-xs ${cls}`}>{status}</span>;
}

function AffiliateFormModal({
  customerId,
  initial,
  onCancel,
  onDone,
}: {
  customerId: string;
  initial: AffiliateDto | null;
  onCancel: () => void;
  onDone: () => Promise<void>;
}) {
  const [status, setStatus] = useState<'active' | 'paused'>(initial?.status ?? 'active');
  const [code, setCode] = useState<string>(initial?.code ?? '');
  const [model, setModel] = useState<'' | CommissionModel>(initial?.commission_model ?? '');
  const [pct, setPct] = useState<string>(
    initial?.commission_pct != null ? String(initial.commission_pct) : '',
  );
  const [flatEur, setFlatEur] = useState<string>(
    initial?.commission_flat_cents != null ? (initial.commission_flat_cents / 100).toFixed(2) : '',
  );
  const [payoutMethod, setPayoutMethod] = useState<'' | PayoutMethod>(initial?.payout_method ?? '');
  const [payoutDetails, setPayoutDetails] = useState('');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isGrant = initial === null;

  async function submit() {
    if (submitting) return;
    setError(null);

    const body: Record<string, unknown> = { status };
    body.commission_model = model === '' ? null : model;

    if (model === 'pct_first_payment') {
      if (pct.trim() === '') {
        body.commission_pct = null;
      } else {
        const n = Number(pct);
        if (!Number.isFinite(n) || n < 0 || n > 100) {
          setError('Commission % must be between 0 and 100.');
          return;
        }
        body.commission_pct = n;
      }
    } else {
      body.commission_pct = null;
    }

    if (model === 'flat') {
      if (flatEur.trim() === '') {
        body.commission_flat_cents = null;
      } else {
        const cents = Math.round(Number(flatEur) * 100);
        if (!Number.isInteger(cents) || cents < 0) {
          setError('Flat amount must be a positive number.');
          return;
        }
        body.commission_flat_cents = cents;
      }
    } else {
      body.commission_flat_cents = null;
    }

    body.payout_method = payoutMethod === '' ? null : payoutMethod;
    // Write-only: only send payout details when the operator typed
    // something, so an edit that leaves it blank keeps the existing value.
    if (payoutDetails.trim() !== '') body.payout_details = payoutDetails.trim();
    body.notes = notes.trim() === '' ? null : notes.trim();
    // OCP-491 - only send a custom code when one is typed. Blank on grant =
    // auto-generate; blank on edit = keep the current code. Same code = no-op
    // server-side.
    if (code.trim() !== '') body.code = code.trim();

    setSubmitting(true);
    try {
      await api(`/v1/admin/accounts/${customerId}/affiliate`, { method: 'POST', body });
      await onDone();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError('That code is already taken - pick another.');
      } else if (err instanceof ApiError && err.status === 400) {
        setError('Invalid code: 3-32 chars, letters/digits/-/_ , must contain a letter.');
      } else {
        setError(err instanceof ApiError ? `Save failed (HTTP ${err.status}).` : 'Save failed.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={isGrant ? 'Grant affiliate status' : 'Edit affiliate'}
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4"
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-xl"
      >
        <h2 className="mb-3 text-base font-semibold">
          {isGrant ? 'Grant affiliate status' : 'Edit affiliate'}
        </h2>
        <div className="space-y-3">
          <label className="block text-sm">
            <span className="text-xs uppercase tracking-wide text-gray-500">
              Ref code {isGrant ? '(blank = auto-generate)' : ''}
            </span>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="e.g. MRBEAST"
              className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 font-mono text-sm uppercase"
            />
            {!isGrant && code.trim() !== '' && initial?.code && code.trim().toUpperCase() !== initial.code.toUpperCase() && (
              <span className="mt-1 block text-xs text-amber-600 dark:text-amber-400">
                Changing the code breaks any already-published ?ref= links (videos, posts) that use the old one.
              </span>
            )}
          </label>

          <label className="block text-sm">
            <span className="text-xs uppercase tracking-wide text-gray-500">Status</span>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as 'active' | 'paused')}
              className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm"
            >
              <option value="active">active</option>
              <option value="paused">paused</option>
            </select>
          </label>

          <label className="block text-sm">
            <span className="text-xs uppercase tracking-wide text-gray-500">Commission model</span>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value as '' | CommissionModel)}
              className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm"
            >
              <option value="">Inherit campaign default</option>
              <option value="pct_first_payment">Percent of first payment</option>
              <option value="flat">Flat per deal</option>
            </select>
          </label>

          {model === 'pct_first_payment' && (
            <label className="block text-sm">
              <span className="text-xs uppercase tracking-wide text-gray-500">
                Commission % (blank = campaign default)
              </span>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={pct}
                onChange={(e) => setPct(e.target.value)}
                className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm"
              />
            </label>
          )}

          {model === 'flat' && (
            <label className="block text-sm">
              <span className="text-xs uppercase tracking-wide text-gray-500">
                Flat amount EUR (blank = campaign default)
              </span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={flatEur}
                onChange={(e) => setFlatEur(e.target.value)}
                className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm"
              />
            </label>
          )}

          <label className="block text-sm">
            <span className="text-xs uppercase tracking-wide text-gray-500">Payout method</span>
            <select
              value={payoutMethod}
              onChange={(e) => setPayoutMethod(e.target.value as '' | PayoutMethod)}
              className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm"
            >
              <option value="">-</option>
              <option value="sepa">SEPA</option>
              <option value="paypal">PayPal</option>
              <option value="other">Other</option>
            </select>
          </label>

          <label className="block text-sm">
            <span className="text-xs uppercase tracking-wide text-gray-500">
              Payout details (IBAN / PayPal){!isGrant && ' - blank keeps current'}
            </span>
            <input
              type="text"
              value={payoutDetails}
              onChange={(e) => setPayoutDetails(e.target.value)}
              placeholder={initial?.payout_details_set ? '•••••••• (on file)' : 'IBAN / PayPal handle'}
              className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm"
            />
          </label>

          <label className="block text-sm">
            <span className="text-xs uppercase tracking-wide text-gray-500">Notes</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm"
            />
          </label>
        </div>

        {error && <div className="mt-2 text-xs text-red-700 dark:text-red-300">{error}</div>}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={() => void submit()}
            className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
          >
            {submitting ? 'Saving…' : isGrant ? 'Grant' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
